"use client";

import { IconHeading } from "@/components/platform/v2/IconHeading";
import Link from "next/link";
import {
  ArrowDown,
  ArrowRight,
  PanelsTopLeft,
  Rocket,
  Smartphone,
  UserRound,
} from "lucide-react";

import { HERO, PROOF } from "@/data/platform";
import { SOLUTIONS } from "@/data/solutions";
import { LIVE_INTEGRATIONS } from "@/data/integrations";
import { DEMO, HOME_PLANS } from "@/data/pricing";

import { PageCta, PageHero } from "@/components/platform/v2/PageEdge";
import { Section, SectionHeading } from "@/components/platform/v2/Section";
import HomeCases from "@/components/platform/v2/HomeCases";
import { ECOSYSTEM_LINKS } from "@/data/ecosystem";

const channels = [
  "Сайт",
  "Приложение",
  "Офлайн",
  "Мессенджеры",
  "Телефон",
  "Менеджер",
];
const contour = [
  "Клиент",
  "CRM",
  "Заявка",
  "Сделка",
  "Задача",
  "Заказ",
  "Оплата",
  "Коммуникация",
  "Документы",
  "Лояльность",
  "Автоматизация",
];
const digital = [
  "Сам зарегистрировался",
  "Приложение / сайт",
  "Профиль",
  "Заявка / покупка",
  "CRM",
];
const offline = [
  "Позвонил / пришёл",
  "Менеджер добавил вручную",
  "Клиент",
  "Сделка",
  "Задача",
  "Документы",
  "Связь с цифровым аккаунтом",
];
const channelCards = [
  {
    icon: Smartphone,
    title: "Приложение",
    lead: "Собственное приложение компании для клиентов и команды. Личный кабинет, каталог, заказы, лояльность, коммуникации и другие клиентские сценарии работают в одном контуре с CRM и данными бизнеса.",
    items: [
      "Личный кабинет",
      "Главная",
      "Каталог без лимита позиций",
      "Рефералы",
      "Бонусы",
      "Брендирование",
      "Документы",
    ],
    to: "/platform/app",
  },
  {
    icon: PanelsTopLeft,
    title: "Сайт",
    lead: "Полноценный сайт бизнеса, а не шаблонный лендинг. Собственная административная панель, управление контентом, каталогом и заявками, аналитика и единая работа с CRM и данными VCENTRE.ONE.",
    items: [
      "Каталог",
      "Формы",
      "Заявки",
      "Заказы",
      "CRM",
      "Собственная админка",
    ],
    to: "/platform/websites",
  },
];

const Flow = ({ items }: { items: string[] }) => (
  <div className="flex flex-col gap-2">
    {items.map((item, index) => (
      <div key={item} className="contents">
        <div className="rounded-xl border border-border/60 px-4 py-3 text-sm font-semibold">
          {item}
        </div>
        {index < items.length - 1 && (
          <ArrowDown className="mx-auto h-4 w-4 text-primary" />
        )}
      </div>
    ))}
  </div>
);

export default function MainPageComponent({}: {}) {
  return (
    <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
      <PageHero
        eyebrow={HERO.eyebrow}
        title={
          <>
            {HERO.title.map((line, i) => (
              <span
                key={line}
                className={`block ${i === 2 ? "text-primary text-glow-yellow" : ""}`}
              >
                {line}
              </span>
            ))}
          </>
        }
        text={HERO.subtitle}
        actions={
          <>
            <Link href="/signin?source=home-hero" className="btn-primary">
              Собрать своё приложение
            </Link>
            <Link href="/platform" className="btn-ghost">
              Как устроена платформа <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        }
        note={
          <p>
            Ответьте на несколько вопросов — покажем концепт под ваш бизнес.
          </p>
        }
        visual={
          <img
            src={"/assets/images/platform/home-mascot-hero.png"}
            alt="Маскот VCENTRE.IT с ноутбуком и фразой «Работаем над вашим успехом»"
            width={1254}
            height={1254}
            loading="eager"
            className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
          />
        }
      />

      <Section tone="quiet">
        <SectionHeading
          eyebrow="Как устроен VCENTRE.ONE"
          title="Каналов может быть много. Система — одна."
          text="Клиент приходит привычным способом, а дальше команда работает с ним в едином контуре."
        />
        <div className="mt-10 grid gap-4 lg:grid-cols-[1fr_auto_0.65fr_auto_1.4fr] lg:items-stretch">
          <div className="flex h-full flex-col">
            <p className="eyebrow mb-4 text-center">Каналы клиента</p>
            <div className="flex flex-1 flex-wrap content-center justify-center gap-2">
              {channels.map((x) => (
                <span key={x} className="pill pill-neutral">
                  {x}
                </span>
              ))}
            </div>
          </div>
          <ArrowRight className="mx-auto hidden self-center text-primary lg:block" />
          <ArrowDown className="mx-auto self-center text-primary lg:hidden" />
          <div className="self-center rounded-2xl border border-primary/40 bg-primary/10 p-6 text-center text-xl font-extrabold text-primary">
            VCENTRE.ONE
          </div>
          <ArrowRight className="mx-auto hidden self-center text-primary lg:block" />
          <ArrowDown className="mx-auto self-center text-primary lg:hidden" />
          <div className="flex h-full flex-col">
            <p className="eyebrow mb-4 text-center">Рабочий контур</p>
            <div className="flex flex-1 flex-wrap content-center justify-center gap-2">
              {contour.map((x) => (
                <span key={x} className="pill pill-neutral">
                  {x}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Онлайн + офлайн"
          title="Неважно, где появился клиент."
          text="Чтобы работать с клиентом в VCENTRE.ONE, ему не обязательно устанавливать приложение или вообще находиться в цифровом контуре."
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="surface">
            <IconHeading
              as="h3"
              icon={<Smartphone className="h-6 w-6 text-primary" />}
              className="display-3 mb-4"
            >
              Цифровой клиент
            </IconHeading>
            <Flow items={digital} />
          </div>
          <div className="surface">
            <IconHeading
              as="h3"
              icon={<UserRound className="h-6 w-6 text-primary" />}
              className="display-3 mb-4"
            >
              Офлайн-клиент
            </IconHeading>
            <Flow items={offline} />
          </div>
        </div>
      </Section>

      <Section tone="quiet">
        <SectionHeading
          eyebrow="Собственные цифровые каналы"
          title="Приложение + сайт"
        />
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {channelCards.map(({ icon: Icon, ...item }) => (
            <article key={item.title} className="surface flex flex-col">
              <IconHeading
                as="h3"
                icon={<Icon className="h-7 w-7 text-primary" />}
                className="display-3"
              >
                {item.title}
              </IconHeading>
              <p className="lede mt-2">{item.lead}</p>
              <div className="my-6 flex flex-wrap gap-2">
                {item.items.map((x) => (
                  <span key={x} className="pill pill-neutral">
                    {x}
                  </span>
                ))}
              </div>
              <Link href={item.to} className="btn-link mt-auto">
                Подробнее <ArrowRight className="h-4 w-4" />
              </Link>
            </article>
          ))}
        </div>
        <p className="mx-auto mt-10 max-w-3xl text-center text-xl font-bold">
          Сайт привлекает. Приложение удерживает.{" "}
          <span className="text-primary">
            VCENTRE.ONE связывает всё вместе.
          </span>
        </p>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Кому подходит"
          title="Шесть моделей работы с клиентом"
        />
        <div className="mt-10 grid gap-x-8 border-y border-border/60 sm:grid-cols-2 lg:grid-cols-3">
          {SOLUTIONS.map((s, i) => (
            <Link
              key={s.id}
              href={s.path}
              className="group flex h-full flex-col border-b border-border/60 py-6 last:border-0"
            >
              <span className="text-xs font-bold text-primary">0{i + 1}</span>
              <h3 className="mt-3 text-lg font-bold group-hover:text-primary">
                {s.nav}
              </h3>
              <p className="body-text mt-2">{s.essence}</p>
              <span className="btn-link mt-auto pt-4">
                Посмотреть решение <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      <HomeCases />

      <Section id="ecosystem" tone="quiet">
        <SectionHeading eyebrow="Экосистема VCENTRE.IT" title="VCENTRE.ONE — центр. Всё остальное подключается по мере необходимости." text="Управляйте клиентами, продажами, приложением, сайтом и процессами в VCENTRE.ONE. Добавляйте расширения, подключайте специализированные платформы и используйте общие ресурсы экосистемы." />
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {[
            { title: "Расширения", text: "Дополнительные возможности и бизнес-механики, которые подключаются к VCENTRE.ONE по мере необходимости. Расширений много: бизнес выбирает только те инструменты, которые нужны его процессам и клиентским сценариям.", to: ECOSYSTEM_LINKS.extensions, cta: "Смотреть расширения" },
            { title: "Платформы VCENTRE.IT", text: "В экосистеме VCENTRE.IT есть самостоятельные платформы для отдельных задач бизнеса. Они могут работать как независимые продукты, а при необходимости связываться с VCENTRE.ONE и становиться частью единого рабочего контура.", to: ECOSYSTEM_LINKS.platforms, cta: "Все платформы" },
            { title: "Магазин", text: "Единое место для покупки и управления дополнительными цифровыми ресурсами и сервисами экосистемы VCENTRE.IT. Ресурсы привязываются к аккаунту и могут использоваться поддерживаемыми платформами.", to: ECOSYSTEM_LINKS.store, cta: "Открыть магазин" },
          ].map((item) => (
            <article key={item.title} className="surface flex flex-col gap-4">
              <h3 className="display-3">{item.title}</h3>
              <p className="body-text">{item.text}</p>
              <Link href={item.to} className="btn-link mt-auto">{item.cta} <ArrowRight className="h-4 w-4" /></Link>
            </article>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Интеграции"
          title="Не обязательно менять то, что уже работает."
        />
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {LIVE_INTEGRATIONS.slice(0, 4).map((i) => (
            <div
              key={i.id}
              className="surface-quiet flex flex-col gap-2 text-center"
            >
              <h3 className="font-bold">{i.name}</h3>
              <p className="body-text">{i.description}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link href="/platform/integrations" className="btn-link">
            Все интеграции и статусы <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>

      <Section tone="quiet">
        <SectionHeading
          eyebrow="Тарифы"
          title="Четыре уровня одного продукта"
          text="Готовое приложение есть во всех тарифах. Выбирайте глубину конструктора, клиентские каналы, расширения и уровень AI."
        />
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {HOME_PLANS.map((p, i) => (
            <article key={p.key} className="border-t border-primary/50 py-6">
              <span className="text-xs font-bold text-primary">0{i + 1}</span>
              <h3 className="display-3 mt-4">{p.name}</h3>
              <p className="body-text mt-3">{p.meaning}</p>
            </article>
          ))}
        </div>
        <div className="mt-6 text-center">
          <p className="body-text mx-auto mb-5 max-w-3xl">{DEMO.text}</p>
          <Link href="/platform/pricing" className="btn-primary">
            Сравнить тарифы
          </Link>
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Компания и доверие"
          title="Продукт экосистемы VCENTRE.IT"
        />
        <div className="mt-10 grid gap-x-8 border-y border-border/60 sm:grid-cols-2">
          {PROOF.slice(0, 4).map((p) => (
            <a
              key={p.title}
              href={p.href}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-between gap-4 border-b border-border/60 py-5"
            >
              <div>
                <p className="font-bold">{p.title}</p>
                <p className="text-xs text-muted-foreground">{p.org}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-primary" />
            </a>
          ))}
        </div>
      </Section>

      <Section>
        <PageCta
          icon={<Rocket className="h-6 w-6 text-primary" />}
          title="Посмотрите, каким может быть VCENTRE.ONE именно для вашего бизнеса."
          text={
            <p>
              Начните с готовой системы и приложения в DEMO. Настройте бренд и
              каталог, посмотрите результат, затем выберите тариф для публикации
              и дальнейшего развития.
            </p>
          }
          actions={
            <>
              <Link href="/signin?source=home-bottom" className="btn-primary">
                Настроить своё приложение
              </Link>
              <Link href="/platform/pricing" className="btn-ghost">
                Посмотреть тарифы
              </Link>
            </>
          }
          visual={
            <img
              src={"/assets/images/platform/home-mascot-cta.png"}
              alt="Маскот VCENTRE.IT с ракетой и фразой «Запускаем бизнес»"
              width={1254}
              height={1254}
              loading="lazy"
              className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
            />
          }
        />
      </Section>
    </div>
  );
}
